import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import axios, { Canceler, CancelTokenSource } from 'axios';

import { DiagnosticDetail, Diagnostics } from '@/generated/fleetAggregationServiceTypes';
import { FeatureToggle, useFeatureToggle } from '@/hooks/useFeatureToggle';

type DiagnosticsDetailState = {
    diagnosticsDetail: DiagnosticDetail | null;
    isLoading: boolean;
    error: Error | null;
    axiosCanceler: Canceler | null;
};

export const useDiagnosticsDetail = (assetId: string, reloadTrigger: object) => {
    const [diagnosticsDetailState, setDamageDetailState] = useState<DiagnosticsDetailState>({ diagnosticsDetail: null, isLoading: true, error: null, axiosCanceler: null });
    const { featureToggleValue: isBrakeLiningsV2Enabled } = useFeatureToggle(FeatureToggle.ENABLE_BRAKE_LININGS_V2);
    const intl = useIntl();

    useEffect(() => {
        const fetchDamageDetail = async () => {
            try {
                const params: Diagnostics.GetDiagnosticById.RequestQuery = {
                    locale: intl.locale,
                };

                const newAxiosCancelTokenSource: CancelTokenSource = axios.CancelToken.source();
                setDamageDetailState((prevState) => {
                    if (prevState.axiosCanceler) {
                        prevState.axiosCanceler();
                    }
                    return { diagnosticsDetail: null, isLoading: true, error: null, axiosCanceler: newAxiosCancelTokenSource.cancel };
                });

                const res = await axios.get<DiagnosticDetail>(`${import.meta.env.VITE_AGGREGATION_SERVICE_URI}/diagnostics${isBrakeLiningsV2Enabled ? '/v2' : ''}/${assetId}`, {
                    params,
                    cancelToken: newAxiosCancelTokenSource.token,
                });
                setDamageDetailState({ diagnosticsDetail: res.data, isLoading: false, error: null, axiosCanceler: null });
            } catch (e: any) {
                if (!axios.isCancel(e)) {
                    setDamageDetailState({ diagnosticsDetail: null, isLoading: false, error: e, axiosCanceler: null });
                }
            }
        };
        fetchDamageDetail();
    }, [intl.locale, assetId, reloadTrigger]);

    return { ...diagnosticsDetailState };
};
